import type { PreparedAttachment } from './attachments';
import { ApiError, type Diary, type DiaryInput, type Draft, type StorageItem } from './types';

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    credentials: 'same-origin',
    ...init,
    headers: init?.body instanceof Blob ? init.headers : { 'Content-Type': 'application/json', ...init?.headers },
  });
  const payload = await response.json().catch(() => ({ error: `请求失败（${response.status}）` })) as Record<string, unknown>;
  if (!response.ok) throw new ApiError(response.status, typeof payload.error === 'string' ? payload.error : '云端请求失败', payload);
  return payload as T;
}

export const api = {
  session: () => request<{ authenticated: true; email?: string }>('/api/session'),
  list: () => request<{ diaries: Diary[]; draft: Draft | null }>('/api/diaries'),
  create: (input: DiaryInput) => request<{ diary: Diary }>('/api/diaries', { method: 'POST', body: JSON.stringify(input) }),
  update: (id: string, input: Partial<DiaryInput> & { version: number }) => request<{ diary: Diary }>(`/api/diaries/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(input) }),
  remove: (id: string) => request<void>(`/api/diaries/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  saveDraft: (input: DiaryInput) => request<{ draft: Draft }>('/api/draft', { method: 'PUT', body: JSON.stringify(input) }),
  deleteDraft: () => request<void>('/api/draft', { method: 'DELETE' }),
  uploadAttachment: (diaryId: string, item: PreparedAttachment) => request<{ attachment: Diary['attachments'][number] }>(`/api/diaries/${encodeURIComponent(diaryId)}/attachments`, {
    method: 'POST',
    body: item.blob,
    headers: {
      'Content-Type': item.blob.type || 'application/octet-stream',
      'X-Shiguang-Original-Name': encodeURIComponent(item.originalName),
      'X-Shiguang-Stored-Name': encodeURIComponent(item.storedName),
      'X-Shiguang-Original-Type': item.originalType,
      'X-Shiguang-Original-Size': String(item.originalSize),
      'X-Shiguang-Stored-Size': String(item.blob.size),
      'X-Shiguang-Kind': item.kind,
    },
  }),
  deleteAttachment: (id: string) => request<void>(`/api/attachments/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  markdownAttachment: async (id: string) => {
    const response = await fetch(`/api/attachments/${encodeURIComponent(id)}?raw=1`, { credentials: 'same-origin' });
    if (!response.ok) throw new ApiError(response.status, response.status === 410 ? '该文件已被删除' : 'Markdown 附件读取失败');
    return response.text();
  },
  storage: () => request<{ items: StorageItem[]; totalBytes: number; objectCount: number }>('/api/storage'),
  deleteStorageAttachment: (attachmentId: string) => request<{ deleted: true; linked: true }>('/api/storage', { method: 'DELETE', body: JSON.stringify({ attachmentId }) }),
  deleteOrphan: (objectKey: string) => request<{ deleted: true; linked: false }>('/api/storage', { method: 'DELETE', body: JSON.stringify({ objectKey }) }),
};
